/* eslint-disable @nx/enforce-module-boundaries */
import { AdddCircleIcon } from '@mybridge/icons';
import { Tag, TagCloseButton, TagLabel, Button } from '@mybridge/ui';
import { useDisclosure } from '@mybridge/ui/hooks';
import { Input, InputGroup, InputRightElement } from '@mybridge/ui/input';
import { HStack, Stack, VStack } from '@mybridge/ui/layout';
import { List, ListItem } from '@mybridge/ui/list';
import { CirclePlus } from '@mybridge/icons';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@mybridge/ui/popover';
import { Spinner } from '@mybridge/ui/spinner';
import { Text } from '@mybridge/ui/text';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSkills } from 'v4/lib/hooks/use-skills';
import { SkillsDropdown } from '../skills-dropdown';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const SkillsTagsInput = ({
  value,
  defaultValue,
  onChange,
  clearOnSelect = false,
  ...props
}) => {
  const inputRef = useRef();
  const wrapperRef = useRef();
  const [search, setSearch] = useState(clearOnSelect ? '' : defaultValue ?? '');
  const disc = useDisclosure();
  const { t } = useTranslator();
  const { data, createLoading, mutateAsync, isLoading } = useSkills({
    search,
  });

  const skills = useMemo(
    () => data?.pages?.reduceRight?.((ac, p) => ac.concat(p?.results), []),
    [data?.pages]
  );

  useEffect(() => {
    if (!clearOnSelect && value !== search) {
      setSearch(value);
    }
  }, [value, clearOnSelect]);

  const handleSelect = (item) => {
    onChange?.(item);
    setSearch(clearOnSelect ? '' : item?.name);
    disc?.onClose?.();
  };

  const handleCreateSkill = async () => {
    try {
      // const resp = await mutateAsync([{ code: null, title: search }]);
      const resp = await mutateAsync({ name: search });
      if (resp) {
        handleSelect(resp);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Popover
      {...disc}
      matchWidth={true}
      initialFocusRef={inputRef}
      placement="bottom-start"
      ref={wrapperRef}
    >
      <PopoverTrigger>
        <InputGroup>
          {/* {isLoading ? (
            <InputRightElement>
              <Spinner />
            </InputRightElement>
          ) : (
            ''
          )} */}
          <Input
            ref={inputRef}
            value={search}
            onKeyUp={(e) => disc?.onOpen?.()}
            h="32px"
            p="5px 12px"
            onChange={(e) => {
              disc?.onOpen?.();
              setSearch(e.target.value);
            }}
            placeholder="Type to search..."
            {...props}
          />
        </InputGroup>
      </PopoverTrigger>
      {disc?.isOpen && search?.length ? (
        <PopoverContent p={0} w="100%" maxH="200px" overflowY="auto">
          {isLoading ? (
            ''
          ) : (
            <PopoverBody p={0}>
              <List>
                {skills?.length > 0
                  ? skills
                      ?.filter?.((c) =>
                        c?.name
                          ?.toLowerCase?.()
                          ?.includes(search?.toLowerCase?.())
                      )
                      ?.map?.((c, cind) => (
                        <ListItem
                          // cursor="pointer"
                          key={cind}
                          _hover={{
                            bg: 'gray.100',
                          }}
                          px={4}
                          py={2}
                          onClick={() => handleSelect(c)}
                        >
                          {c.name}
                        </ListItem>
                      ))
                  : ''}
                {search?.length ? (
                  <ListItem
                    cursor="pointer"
                    _hover={{
                      bg: 'gray.100',
                    }}
                    px={4}
                    py={2}
                    onClick={handleCreateSkill}
                    leftIcon={<AdddCircleIcon />}
                  >
                    <HStack>
                      {createLoading ? <Spinner /> : ''}
                      <Text>{t('myDisk.create') || "Create"} {search}</Text>
                    </HStack>
                  </ListItem>
                ) : (
                  ''
                )}
              </List>
            </PopoverBody>
          )}
        </PopoverContent>
      ) : (
        ''
      )}
    </Popover>
  );
};

export const SkillsTaggedInput = ({
  value,
  defaultValue,
  onChange,
  keyName = 'name',
  round = true,
  ...props
}) => {
  const [selected, setSelected] = useState(defaultValue ?? []);
  const [mode, setMode] = useState();
  const { t } = useTranslator();
  useEffect(() => {
    if (JSON.stringify(value) !== JSON.stringify(selected)) {
      setSelected(value);
    }
  }, [value]);

  const handleChange = (val) => {
    // console.log(val);
    // console.log(selected);
    const sel = [...(selected ?? [])];
    if (!sel?.includes(val?.[keyName])) {
      sel.push(val?.[keyName]);
      onChange_([...(sel ?? [])]);
      setMode(null);
    }
  };

  const handleRemove = (val) => (e) => {
    const sel = selected?.filter?.((s) => s !== val);
    onChange_([...(sel ?? [])]);
  };

  const onChange_ = (val) => {
    setSelected(val);
    onChange?.(val);
  };

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  return (
    <VStack flexWrap="wrap" alignItems="flex-start">
      {mode === 'add' ? (
        // <SkillsTagsInput
        //   inputGroupProps={{
        //     maxW: '200px',
        //   }}
        //   autoFocus
        //   borderRadius={round ? 'full' : '5'}
        //   maxW="200px"
        //   onEscape={(e) => setMode(null)}
        //   onChange={handleChange}
        //   value={value}
        //   defaultValue={defaultValue}
        //   clearOnSelect={true}
        // />
        <SkillsDropdown
          inputGroupProps={{
            maxW: '200px',
          }}
          autoFocus
          borderRadius={round ? 'full' : '5'}
          maxW="200px"
          onEscape={(e) => setMode(null)}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          clearOnSelect={true}
          h="10"
          existing={selected}
        />
      ) : (
        ''
      )}

      {!mode ? (
        <Button
          border="1px solid"
          borderColor="gray.300"
          fontSize="sm"
          bgColor="brandGray.200"
          onClick={(e) => setMode('add')}
          h="32px"
          p="5px 12px"
          borderRadius={round ? 'full' : '5'}
          rightIcon={<CirclePlus width={20} height={20} />}
        >
          {t('profile.addSkills') || "Add Skills"}
        </Button>
      ) : (
        ''
      )}
      {/* <div flexWrap="wrap"> */}
        <Stack justifyContent="flex-start" alignItems="flex-start" flexWrap="wrap">
          {selected?.length ? (
            <>
              {selected?.map((s, sIndex) => (
                <Tag
                  border="1px solid"
                  borderColor="gray.300"
                  fontSize="sm"
                  bgColor="brandGray.200"
                  p="5px 12px"
                  borderRadius={round ? 'full' : '5'}
                  key={sIndex}
                >
                  <TagLabel pl={2}>{s}</TagLabel>
                  <TagCloseButton onClick={handleRemove(s)} px={3} />
                </Tag>
              ))}
            </>
          ) : null}
        </Stack>
      {/* </div> */}
    </VStack>
  );
};
